import { useEffect, useMemo, useState } from "react";
import { Plus, X, Mail, Phone, MessageSquare, Globe } from "lucide-react";
import { api, StructureContact } from "../lib/api";
import { Spinner } from "./Spinner";
import { InlineAlert } from "./InlineAlert";
import { ContactAvatar } from "./ContactAvatar";

interface ContactPickerProps {
  structureId: string;
  selectedContactIds: string[];
  contactRoles?: Record<string, string>;
  onChange: (contactIds: string[], contactRoles: Record<string, string>) => void;
}

export function ContactPicker({ structureId, selectedContactIds, contactRoles = {}, onChange }: ContactPickerProps) {
  const [availableContacts, setAvailableContacts] = useState<StructureContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadContacts();
  }, [structureId]);

  const loadContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listContacts(structureId, false);
      setAvailableContacts(data.contacts);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Kontakte");
    } finally {
      setLoading(false);
    }
  };

  const selectedContacts = availableContacts.filter(c => selectedContactIds.includes(c.id));
  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return availableContacts;
    return availableContacts.filter((c) => {
      const values = [c.name, c.role, c.email, c.phone, c.signal, c.matrix, c.telegram, c.website]
        .filter(Boolean)
        .map((v) => v!.toString().toLowerCase());
      return values.some((v) => v.includes(term));
    });
  }, [availableContacts, search]);
  const unselectedContacts = filteredContacts.filter(c => !selectedContactIds.includes(c.id));
  const selectedContactsFiltered = filteredContacts.filter(c => selectedContactIds.includes(c.id));

  const addContact = (contactId: string) => {
    if (selectedContactIds.includes(contactId)) return;
    const nextRoles = { ...contactRoles, [contactId]: contactRoles[contactId] || "primary" };
    onChange([...selectedContactIds, contactId], nextRoles);
  };

  const removeContact = (contactId: string) => {
    const nextIds = selectedContactIds.filter(id => id !== contactId);
    const nextRoles = { ...contactRoles };
    delete nextRoles[contactId];
    onChange(nextIds, nextRoles);
  };

  if (loading) {
    return (
      <Spinner label="Lade Kontakte..." size="sm" className="text-[var(--color-muted)]" />
    );
  }

  if (error) {
    return (
      <InlineAlert tone="error" message={error} dismissible onClose={() => setError(null)} />
    );
  }

  return (
    <div className="space-y-3">
      {/* Selected Contacts */}
      {(selectedContactsFiltered.length > 0 || (selectedContacts.length > 0 && !search)) && (
        <div className="space-y-2">
          {(search ? selectedContactsFiltered : selectedContacts).map(contact => (
            <div
              key={contact.id}
              className="flex items-start gap-3 p-3 bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)]"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-col gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <ContactAvatar contact={contact} size={32} enablePreview className="flex-shrink-0" />
                    <span className="font-medium text-[var(--color-text)] truncate">{contact.name}</span>
                    {contact.is_board_member && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700 whitespace-nowrap">
                        Vorstand
                      </span>
                    )}
                  </div>
                  {contact.role && (
                    <div className="text-xs text-[var(--color-muted)] ml-10">{contact.role}</div>
                  )}
                </div>

                {/* Contact Details */}
                <div className="space-y-1 text-xs text-[var(--color-muted)] mt-1 ml-10 min-w-0">
                  {contact.email && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <a href={`mailto:${contact.email}`} className="hover:text-[var(--color-text)] truncate">{contact.email}</a>
                    </div>
                  )}
                  {contact.phone && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Phone className="w-3 h-3 flex-shrink-0" />
                      <a href={`tel:${contact.phone}`} className="hover:text-[var(--color-text)] truncate">{contact.phone}</a>
                    </div>
                  )}
                  {contact.signal && (
                    <div className="flex items-center gap-2 min-w-0">
                      <MessageSquare className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate max-w-full">Signal: {contact.signal}</span>
                    </div>
                  )}
                  {contact.matrix && (
                    <div className="flex items-center gap-2 min-w-0">
                      <MessageSquare className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate break-all max-w-full">Matrix: {contact.matrix}</span>
                    </div>
                  )}
                  {contact.telegram && (
                    <div className="flex items-center gap-2 min-w-0">
                      <MessageSquare className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate max-w-full">Telegram: @{contact.telegram}</span>
                    </div>
                  )}
                  {contact.website && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Globe className="w-3 h-3 flex-shrink-0" />
                      <a href={contact.website} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--color-text)] truncate break-all">
                        {contact.website}
                      </a>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between gap-2 mt-2 ml-6">
                  <span className="text-[11px] text-[var(--color-muted)]">Rolle im Knoten</span>
                  <select
                    value={contactRoles[contact.id] || "primary"}
                    onChange={(e) => {
                      const nextRoles = { ...contactRoles, [contact.id]: e.target.value };
                      onChange(selectedContactIds, nextRoles);
                    }}
                    className="text-xs border rounded px-2 py-1 bg-[var(--color-bg-secondary)]"
                  >
                    <option value="primary">Primär</option>
                    <option value="backup">Backup</option>
                  </select>
                </div>
              </div>

              <button
                onClick={() => removeContact(contact.id)}
                className="p-1 hover:bg-[var(--color-bg-tertiary)] rounded text-[var(--color-muted)] hover:text-red-500"
                title="Kontakt entfernen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Contact Button */}
      {!isPickerOpen && (
        <button
          onClick={() => {
            setSearch("");
            setIsPickerOpen(true);
          }}
          disabled={unselectedContacts.length === 0}
          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border ${
            unselectedContacts.length === 0
              ? "text-[var(--color-muted)] border-[var(--color-border)]"
              : "text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-blue-300 dark:border-blue-700"
          }`}
        >
          <Plus className="w-4 h-4" />
          {unselectedContacts.length === 0 ? "Alle Kontakte hinzugefügt" : "Kontakt hinzufügen"}
        </button>
      )}

      {/* Contact Picker Dropdown */}
      {isPickerOpen && (
        <div className="border border-[var(--color-border)] rounded-lg bg-[var(--color-bg-secondary)] overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b border-[var(--color-border)]">
            <h4 className="text-sm font-semibold text-[var(--color-text)]">
              Kontakt auswählen
            </h4>
            <button
              onClick={() => setIsPickerOpen(false)}
              className="p-1 hover:bg-[var(--color-bg-tertiary)] rounded text-[var(--color-muted)]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-3 border-b border-[var(--color-border)]">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kontakt suchen..."
              className="w-full rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1 text-sm"
            />
          </div>

          {unselectedContacts.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)] p-3">
              Alle Kontakte wurden bereits hinzugefügt.
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto overscroll-contain" style={{ scrollbarWidth: 'thin' }}>
              <div className="p-2 space-y-1">
                {unselectedContacts.map(contact => (
                  <button
                    key={contact.id}
                    onClick={() => {
                      addContact(contact.id);
                      setIsPickerOpen(false);
                      setSearch("");
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[var(--color-bg-tertiary)] rounded-lg text-sm transition-colors"
                  >
                    <ContactAvatar contact={contact} size={28} enablePreview={false} className="flex-shrink-0" />
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <span className="text-[var(--color-text)] truncate">{contact.name}</span>
                        {contact.role && (
                          <span className="text-xs text-[var(--color-muted)] truncate">({contact.role})</span>
                        )}
                      </div>
                      {contact.email && (
                        <div className="text-xs text-[var(--color-muted)] truncate">{contact.email}</div>
                      )}
                    </div>
                    <select
                      value={contactRoles[contact.id] || "primary"}
                      onChange={(e) => {
                        const nextRoles = { ...contactRoles, [contact.id]: e.target.value };
                        onChange(selectedContactIds, nextRoles);
                      }}
                      className="text-xs border rounded px-2 py-0.5 bg-transparent"
                    >
                      <option value="primary">Primär</option>
                      <option value="backup">Backup</option>
                    </select>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {unselectedContacts.length === 0 && selectedContacts.length === 0 && (
        <p className="text-sm text-[var(--color-muted)]">
          Keine Kontakte verfügbar. Erstelle zuerst Kontakte unter Einstellungen → Kontakte.
        </p>
      )}
    </div>
  );
}
